'use client';

import { useState, useRef, useEffect } from 'react';
import { askOracle } from '@/lib/actions';
import { Send, Bot, User, Sparkles, Trash2, Wand2 } from 'lucide-react';
import Link from 'next/link';
import MarkdownRenderer from '@/components/MarkdownRenderer';
import OracleCommitPanel from '@/components/OracleCommitPanel';

export default function ChatPage() {
  const [messages, setMessages] = useState<{ role: string; content: string }[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [showCommit, setShowCommit] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  const handleSend = async () => {
    if (!input.trim() || loading) return;

    const userMsg = { role: 'user', content: input };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setLoading(true);

    try {
      const response = await askOracle(input);
      setMessages(prev => [...prev, { role: 'bot', content: response }]);
    } catch {
      setMessages(prev => [...prev, { role: 'bot', content: "I'm having trouble connecting to the Oracle. Make sure llama-server is running on port 8520!" }]);
    } finally {
      setLoading(false);
    }
  };

  const clearChat = () => {
    if (confirm("Clear our current brainstorm?")) setMessages([]);
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 flex flex-col h-screen overflow-hidden relative">
      <header className="p-6 border-b border-slate-800 bg-slate-900/50 flex items-center justify-between shrink-0">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-purple-600 rounded-lg shadow-lg shadow-purple-900/40">
            <Sparkles className="text-white" size={24} />
          </div>
          <div>
            <h1 className="text-xl font-serif font-bold">The Oracle</h1>
            <p className="text-xs text-slate-400 italic">Your creative partner for The Sentinels</p>
          </div>
        </div>
        <div className="flex items-center gap-6">
          {messages.length >= 2 && (
            <button
              onClick={() => setShowCommit(true)}
              className="flex items-center gap-2 text-xs text-purple-300 hover:text-purple-200 border border-purple-500/20 hover:border-purple-500/40 px-3 py-1.5 rounded-lg bg-purple-900/10 hover:bg-purple-900/20 transition-all font-mono uppercase tracking-widest"
            >
              <Wand2 size={14} /> Save ideas
            </button>
          )}
          {messages.length > 0 && (
            <button onClick={clearChat} className="text-xs text-slate-500 hover:text-red-400 flex items-center gap-2 transition-colors font-mono uppercase tracking-widest">
              <Trash2 size={14} /> Clear Chat
            </button>
          )}
          <Link href="/" className="text-sm text-purple-400 hover:text-purple-300">Back to Gallery</Link>
        </div>
      </header>

      {/* Chat Messages */}
      <div className="flex-1 overflow-y-auto p-6 space-y-8">
        {messages.length === 0 && (
          <div className="h-full flex flex-col items-center justify-center text-center max-w-md mx-auto opacity-50">
            <Bot size={64} className="mb-4 text-purple-500" />
            <h2 className="text-2xl font-serif mb-2 italic">Greetings, Sentinel.</h2>
            <p className="leading-relaxed">I am the Oracle. I hold the knowledge of your world's heroes, realms, and myths. What shall we brainstorm today?</p>
          </div>
        )}

        <div className="max-w-4xl mx-auto space-y-8">
          {messages.map((m, i) => (
            <div key={i} className={`flex gap-6 ${m.role === 'user' ? 'justify-end' : ''}`}>
              {m.role === 'bot' && (
                <div className="w-10 h-10 rounded-full bg-purple-900 flex items-center justify-center border border-purple-500 shadow-glow shrink-0">
                  <Bot size={20} className="text-purple-300" />
                </div>
              )}
              <div className={`max-w-[85%] p-6 rounded-3xl ${
                m.role === 'user'
                  ? 'bg-purple-600 text-white rounded-tr-none shadow-xl shadow-purple-900/20'
                  : 'bg-slate-900 border border-slate-800 rounded-tl-none leading-relaxed shadow-xl'
              }`}>
                {m.role === 'bot' ? <MarkdownRenderer content={m.content} /> : <p>{m.content}</p>}
              </div>
              {m.role === 'user' && (
                <div className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center border border-slate-700 shrink-0">
                  <User size={20} className="text-slate-400" />
                </div>
              )}
            </div>
          ))}

          {loading && (
            <div className="flex gap-6 animate-pulse">
              <div className="w-10 h-10 rounded-full bg-purple-900/50 shrink-0" />
              <div className="bg-slate-900 border border-slate-800 p-6 rounded-3xl rounded-tl-none w-64" />
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Input Area */}
      <div className="p-6 border-t border-slate-800 bg-slate-900/50 shrink-0">
        <div className="max-w-4xl mx-auto flex gap-4">
          <input
            type="text"
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSend()}
            placeholder="Ask about a character, location, or get a new idea..."
            className="flex-1 bg-slate-800 border border-slate-700 rounded-2xl px-6 py-4 focus:outline-none focus:ring-2 focus:ring-purple-500/50 text-slate-100 placeholder-slate-500 transition-all shadow-inner"
          />
          <button
            onClick={handleSend}
            disabled={loading}
            className="bg-purple-600 hover:bg-purple-500 text-white p-4 rounded-2xl transition-all shadow-lg shadow-purple-900/40 disabled:opacity-50 group"
          >
            <Send size={24} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
          </button>
        </div>
      </div>

      {/* Commit overlay — renders on top of everything */}
      {showCommit && (
        <OracleCommitPanel messages={messages} onClose={() => setShowCommit(false)} />
      )}
    </div>
  );
}
